import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Outlet, useMatch, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Inbox, Layers, Lock, MessageCircleOff, TriangleAlert } from 'lucide-react';
import { Header } from '@/components/Header';
import { TitleMenu } from '@/components/TitleMenu';
import { EmptyState } from '@/components/EmptyState';
import { ChannelRail } from '@/components/inbox/ChannelRail';
import { FunnelSpectrum } from '@/components/inbox/FunnelSpectrum';
import { ConversationRow } from '@/components/inbox/ConversationRow';
import { ListSkeleton } from '@/components/inbox/ListSkeleton';
import { useProject } from '@/contexts/ProjectProvider';
import { useCwSession } from '@/hooks/useCwSession';
import {
  useAllStages, useConversationList, useInboxes, useListMeta, useUnreadCounts,
} from '@/hooks/useInboxData';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { useInboxUi } from '@/store/inboxUi';
import { flatten, hasNextPage, type ListData } from '@/lib/convList';
import { cwKeys } from '@/lib/cwCache';
import { FOLDERS, folderToQuery, type Folder, type ListQuery } from '@/lib/inboxQuery';
import type { CwInboxStage, InboxConversation } from '@/api/inboxTypes';
import { ApiError } from '@/lib/http';

const ROW_ESTIMATE = 76;

/** Страница «Входящие»: проверяет доступ, рисует список; переписка открывается поверх (Outlet). */
export default function InboxPage() {
  const { status, error: projectError, active, projectId, chatwootAccountId, reload } = useProject();
  const s = useCwSession();
  const overlay = !!useMatch('/inbox/:accountId/:displayId');
  const resetFor = useInboxUi((u) => u.resetFor);

  // Смена проекта (кабинета) обнуляет выбор папки/канала/стадии.
  useEffect(() => { resetFor(chatwootAccountId); }, [chatwootAccountId, resetFor]);

  const шапка = (subtitle: string) => <Header title="Входящие" subtitle={subtitle} />;
  const экран = (subtitle: string, body: ReactNode) => <>{шапка(subtitle)}<div className="scr">{body}</div></>;

  let content: ReactNode;
  if (status === 'loading' || status === 'idle') {
    content = экран('Загружаем проект…', <ListSkeleton />);
  } else if (status === 'error') {
    content = экран('Проект недоступен', <EmptyState icon={TriangleAlert} title="Не удалось загрузить проекты" text={projectError ?? undefined} action={{ label: 'Повторить', onClick: () => void reload() }} />);
  } else if (!projectId) {
    content = экран('Проект не выбран', <EmptyState icon={Layers} title="Проект не выбран" text="Выберите проект в разделе «Ещё»: переписки принадлежат проекту." />);
  } else if (chatwootAccountId === null) {
    content = экран(active?.name ?? '', <EmptyState icon={Inbox} title="Переписки для проекта не подключены" text="Для этого проекта ещё не заведён кабинет Переписок. Выберите другой проект в «Ещё»." />);
  } else if (s.profile.isPending) {
    content = экран(active?.name ?? '', <ListSkeleton />);
  } else if (s.profile.isError && !(s.profile.error instanceof ApiError && [401, 403, 404].includes(s.profile.error.status))) {
    content = экран(active?.name ?? '', <EmptyState icon={TriangleAlert} title="Переписки не отвечают" text={s.profile.error.message} action={{ label: 'Повторить', onClick: () => void s.profile.refetch() }} />);
  } else if (!s.inAccount) {
    content = экран(active?.name ?? '', <EmptyState icon={Lock} title="У вас нет доступа к Перепискам в этом проекте" text="Попросите руководителя добавить вас в кабинет Переписок." />);
  } else {
    content = <InboxContent accountId={chatwootAccountId} myUserId={s.myUserId} projectName={active?.name ?? ''} />;
  }

  return (
    <>
      {/* Под открытой перепиской список остаётся живым (прокрутка и выбор не теряются), но недоступен. */}
      <div className="flex-1 min-h-0 flex flex-col" inert={overlay} aria-hidden={overlay}>{content}</div>
      <Outlet />
    </>
  );
}

function emptyText(folder: Folder, filtered: boolean): { title: string; text: string } {
  if (filtered) return { title: 'Здесь пока пусто', text: 'Попробуйте другой канал, стадию или «Все переписки».' };
  if (folder === 'mine') return { title: 'Своих переписок нет', text: 'Назначенные на вас переписки появятся здесь. Свободные — в «Все переписки».' };
  if (folder === 'unattended') return { title: 'Все ответы отправлены', text: 'Клиенты, ждущие ответа, появятся здесь.' };
  return { title: 'Переписок пока нет', text: 'Когда клиенты напишут, переписки появятся здесь.' };
}

function InboxContent({ accountId, myUserId, projectName }: { accountId: number; myUserId: number | null; projectName: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const ui = useInboxUi();
  const [menu, setMenu] = useState(false);

  const inboxesQ = useInboxes(accountId, true);
  const inboxes = useMemo(() => inboxesQ.data ?? [], [inboxesQ.data]);
  const stagesAll = useAllStages(accountId, inboxesQ.data, true);
  const unreadQ = useUnreadCounts(accountId, true);
  const unread = unreadQ.data ?? null;

  // Какая воронка показана: при выбранном канале — его; при «Все» — выбранная вручную или первая с стадиями.
  const inboxesWithStages = inboxes.filter((i) => (stagesAll.byInbox.get(i.id)?.length ?? 0) > 0);
  const funnelInboxId = ui.channel !== 'all' ? ui.channel : (ui.funnelInboxId ?? inboxesWithStages[0]?.id ?? null);
  const funnelStages: CwInboxStage[] = funnelInboxId !== null ? stagesAll.byInbox.get(funnelInboxId) ?? [] : [];
  const funnelName = inboxes.find((i) => i.id === funnelInboxId)?.name ?? '';
  const stage = funnelStages.find((s) => s.id === ui.stageId) ?? null;

  const query: ListQuery = useMemo(
    () => folderToQuery(ui.folder, {
      inboxId: ui.channel === 'all' ? null : ui.channel,
      stage: stage ? { id: stage.id, externalId: stage.external_id } : null,
    }),
    [ui.folder, ui.channel, stage],
  );
  const listQ = useConversationList(accountId, query, true);
  const data: ListData | undefined = listQ.data;
  const rows: InboxConversation[] = useMemo(() => flatten(data), [data]);
  const more = hasNextPage(data);

  // «N ждут ответа» — число ждущих в текущей выборке (канал/стадия учитываются).
  const waitingQ = useListMeta(accountId, { ...query, unattended: true }, true);
  const waiting = waitingQ.data ? (ui.folder === 'mine' ? waitingQ.data.mine_count : waitingQ.data.all_count) : null;

  const scrollRef = useRef<HTMLDivElement>(null);
  const key = `${ui.folder}|${ui.channel}|${ui.stageId ?? ''}`;
  const keyRef = useRef(key);
  const setScroll = useInboxUi((u) => u.setScroll);

  const virt = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_ESTIMATE,
    overscan: 8,
    initialOffset: ui.scroll[key] ?? 0,
    getItemKey: (i) => rows[i]?.id ?? i,
  });
  const items = virt.getVirtualItems();

  // Выбрали другую папку/канал — возвращаем ту прокрутку, что была у этой выборки.
  useEffect(() => {
    if (keyRef.current === key) return;
    keyRef.current = key;
    virt.scrollToOffset(useInboxUi.getState().scroll[key] ?? 0);
  }, [key, virt]);

  // Запоминаем прокрутку при уходе со страницы (вкладки, переписка не размонтируют список).
  useEffect(() => {
    const el = scrollRef.current;
    return () => { if (el) setScroll(keyRef.current, el.scrollTop); };
  }, [setScroll]);

  // Подгрузка следующей страницы, когда дошли до конца.
  const lastIndex = items.length ? items[items.length - 1].index : -1;
  useEffect(() => {
    if (more && !listQ.isFetchingNextPage && lastIndex >= rows.length - 6 && rows.length > 0) {
      void listQ.fetchNextPage();
    }
  }, [more, lastIndex, rows.length, listQ]);

  const refresh = useCallback(async () => {
    // Сначала оставляем первую страницу, иначе перезапрос потянул бы все загруженные.
    qc.setQueryData<ListData>(cwKeys.convs(accountId, query), (d) =>
      d ? { pages: d.pages.slice(0, 1), pageParams: d.pageParams.slice(0, 1) } : d);
    await Promise.all([
      listQ.refetch(),
      qc.invalidateQueries({ queryKey: cwKeys.unread(accountId) }),
      qc.invalidateQueries({ queryKey: ['cw', 'meta', accountId] }),
    ]);
  }, [qc, accountId, query, listQ]);
  const ptr = usePullToRefresh(scrollRef, refresh);

  const open = useCallback((c: InboxConversation) => navigate(`/inbox/${accountId}/${c.id}`), [navigate, accountId]);

  const folderLabel = FOLDERS.find((f) => f.key === ui.folder)?.label ?? 'Мои';
  const subtitle = waiting === null
    ? projectName
    : waiting > 0
      ? <><em className="tnum">{waiting}</em> ждут ответа</>
      : 'Все ответы отправлены';

  const showSkeleton = listQ.isPending && !data;
  const filtered = ui.channel !== 'all' || ui.stageId !== null;
  const multiInbox = inboxes.length > 1 && ui.channel === 'all';
  const empty = emptyText(ui.folder, filtered);

  return (
    <>
      <Header
        title={folderLabel}
        subtitle={subtitle}
        onTitleClick={() => setMenu((v) => !v)}
        titleExpanded={menu}
        menu={menu && (
          <TitleMenu
            items={FOLDERS.map((f) => ({ key: f.key, label: f.label }))}
            selected={ui.folder}
            onSelect={(k) => ui.setFolder(k as Folder)}
            onClose={() => setMenu(false)}
          />
        )}
      />
      <ChannelRail
        inboxes={inboxes}
        selected={ui.channel}
        onSelect={ui.setChannel}
        unread={unread ? { all: unread.all_count, byInbox: unread.inboxes } : null}
      />
      <FunnelSpectrum
        stages={funnelStages}
        counts={unread?.stages ?? (unread ? {} : null)}
        selectedStageId={stage?.id ?? null}
        onSelect={ui.setStage}
        funnelName={funnelName}
        options={ui.channel === 'all' ? inboxesWithStages.map((i) => ({ id: i.id, name: i.name })) : undefined}
        onPickFunnel={ui.setFunnelInbox}
      />
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="ptr" style={{ height: ptr.height }} aria-hidden={!ptr.busy}>
          {ptr.busy ? <span className="spinner" role="status" aria-label="Обновляем" /> : ptr.pulling ? 'Отпустите, чтобы обновить' : null}
        </div>
        <div className="scr relative" ref={scrollRef}>
          {showSkeleton && <ListSkeleton />}
          {listQ.isError && !data && (
            <EmptyState icon={TriangleAlert} title="Переписки не загрузились" text={listQ.error.message} action={{ label: 'Повторить', onClick: () => void listQ.refetch() }} />
          )}
          {!showSkeleton && !listQ.isError && rows.length === 0 && (
            <EmptyState icon={MessageCircleOff} title={empty.title} text={empty.text} />
          )}
          {rows.length > 0 && (
            <div className="relative w-full" style={{ height: virt.getTotalSize() }}>
              {items.map((vi) => {
                const c = rows[vi.index];
                if (!c) return null;
                const st = c.crmStageId ? stagesAll.byInbox.get(c.inboxId)?.find((x) => x.external_id === c.crmStageId) ?? null : null;
                return (
                  <div
                    key={vi.key}
                    data-index={vi.index}
                    ref={virt.measureElement}
                    className="absolute top-0 left-0 w-full"
                    style={{ transform: `translateY(${vi.start}px)` }}
                  >
                    <ConversationRow
                      conv={c}
                      stage={st}
                      myUserId={myUserId}
                      inboxName={multiInbox ? inboxes.find((i) => i.id === c.inboxId)?.name : undefined}
                      onOpen={open}
                    />
                  </div>
                );
              })}
            </div>
          )}
          {listQ.isFetchingNextPage && <div className="py-4 flex justify-center"><span className="spinner size-6" role="status" aria-label="Загружаем ещё" /></div>}
          {listQ.isError && !!data && (
            <div className="py-3 text-center text-sm text-muted">
              Не удалось обновить.{' '}
              <button type="button" className="text-accent font-semibold min-h-11 px-2" onClick={() => void listQ.refetch()}>Повторить</button>
            </div>
          )}
          <div className="h-6" />
        </div>
      </div>
    </>
  );
}
